import { useEffect, useState, type FC } from 'react';
import { getPublicCrispConfig } from 'interfaces/plugin';

interface CrispChatLoaderProps {
    ownerId: string;
    userEmail?: string;
    userName?: string;
    userId?: string;
}

declare global {
    interface Window {
        $crisp: any[];
        CRISP_WEBSITE_ID: string;
    }
}

const CrispChatLoader: FC<CrispChatLoaderProps> = ({ ownerId, userEmail, userName, userId }) => {
    const [crispConfig, setCrispConfig] = useState<any>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const loadCrispConfig = async () => {
            try {
                const config = await getPublicCrispConfig(ownerId);

                if (!config || !config.enabled || !config.websiteId) {
                    console.log('Crisp chat non configuré pour cet utilisateur');
                    return;
                }

                console.log('Config Crisp récupérée:', config);
                setCrispConfig(config);
            } catch (error) {
                console.error('Erreur lors du chargement de la config Crisp:', error);
            }
        };

        loadCrispConfig();
    }, [ownerId]);

    useEffect(() => {
        if (!crispConfig || loaded) return;

        console.log('Initialisation de Crisp avec websiteId:', crispConfig.websiteId);

        try {
            const sessionKey = `crisp-client/session/${crispConfig.websiteId}`;
            if (localStorage.getItem(sessionKey)) {
                localStorage.removeItem(sessionKey);
                console.log('Session Crisp précédente nettoyée');
            }
        } catch (e) {
            console.warn('Impossible de nettoyer le localStorage:', e);
        }

        window.$crisp = [];
        window.CRISP_WEBSITE_ID = crispConfig.websiteId;

        if (process.env.NODE_ENV === 'development') {
            window.$crisp.push(['safe', true]);
        }

        const script = document.createElement('script');
        script.src = 'https://client.crisp.chat/l.js';
        script.async = true;
        script.id = 'crisp-customer-chat';

        script.onload = () => {
            console.log('Script Crisp chargé');
            setLoaded(true);

            const checkCrispReady = setInterval(() => {
                if (window.$crisp && window.$crisp.push) {
                    clearInterval(checkCrispReady);

                    if (userId && userEmail) {
                        // Avec userId : crée une conversation unique par utilisateur
                        window.$crisp.push(['set', 'user:email', [userId, userEmail]]);
                        console.log('User ID configuré:', userId);
                        console.log('Email utilisateur configuré:', userEmail);
                    } else if (userEmail) {
                        // Sans userId : utilise juste l'email
                        window.$crisp.push(['set', 'user:email', [userEmail]]);
                        console.log('Email utilisateur configuré:', userEmail);
                    }

                    if (userName) {
                        window.$crisp.push(['set', 'user:nickname', [userName]]);
                        console.log('Nom utilisateur configuré:', userName);
                    }

                    if (crispConfig.variables) {
                        if (crispConfig.variables.color) {
                            window.$crisp.push(['set', 'session:data', [['color', crispConfig.variables.color]]]);
                            console.log('Couleur configurée:', crispConfig.variables.color);
                        }
                        if (crispConfig.variables.position === 'left') {
                            window.$crisp.push(['config', 'position:reverse', [true]]);
                            console.log('Position configurée: gauche');
                        }
                    }

                    window.$crisp.push(['do', 'chat:show']);
                    console.log('Crisp chat affiché');
                }
            }, 100);

            setTimeout(() => clearInterval(checkCrispReady), 5000);
        };

        script.onerror = (error) => {
            console.error('Erreur lors du chargement du script Crisp:', error);
        };

        document.head.appendChild(script);
        console.log('Script Crisp ajouté au DOM');

        return () => {
            console.log('Nettoyage de Crisp');

            if (window.$crisp) {
                window.$crisp.push(['do', 'chat:hide']);
            }

            const crispScript = document.getElementById('crisp-customer-chat');
            if (crispScript) {
                crispScript.remove();
            }

            const crispWidget = document.querySelector('.crisp-client');
            if (crispWidget) {
                crispWidget.remove();
            }

            delete window.$crisp;
            delete window.CRISP_WEBSITE_ID;
        };
    }, [crispConfig, loaded, userEmail, userName, userId]);

    return null;
};

export default CrispChatLoader;